// ⚠ The `.ts` extension is deliberate and is not the house style by accident: this module is
// exercised by `node --experimental-strip-types` (the repo has no test runner), and node resolves
// no extension for you. `allowImportingTsExtensions` is on, and Vite resolves it unchanged.
import { formatBytes, formatCents } from './general.ts'

/**
 * The axes whose `overage_cents` the projected bill adds up.
 *
 * ⚠⚠ THIS SET MUST EQUAL THE ROUTER'S `billing.meteredAxes` (`src/billing/usage.go`), AND
 * NOTHING ENFORCES THAT. The router holds the single definition of what is metered; the app
 * cannot import Go, so this is the second copy and the two must be changed together. A new
 * metered axis added there and not here under-reports the projection in silence — which is
 * exactly how `compute` came to be missing (app#152). When you edit `meteredAxes`, edit this.
 *
 * ⚠ Why `storage` and `nodes` are NOT here, although they were: router migration `0108` dropped
 * `storage_rate_cents_per_gb_month` and `node_overage_cents`, so `get_org_usage` returns those
 * axes with an overage of a structural zero. Summing them added nothing and never will. They were
 * not a bug — they are why the old set looked deliberate: three named axes read as a considered
 * choice while one of the two axes that can actually bill was absent.
 *
 * ⚠ The two retired axes are still RETURNED, still have allowances, and are still drawn on the
 * Usage panel. This set is about MONEY, not about what a customer may see.
 */
export const BILLED_OVERAGE_AXES: readonly string[] = ['bandwidth', 'compute']

const BILLED_OVERAGE = new Set(BILLED_OVERAGE_AXES)

/** Period-to-date overage in cents across the metered axes. */
export const billedOverageCents = (axes: readonly { axis: string; overageCents: number }[]): number =>
  axes.filter(a => BILLED_OVERAGE.has(a.axis)).reduce((sum, a) => sum + a.overageCents, 0)

/**
 * Render one axis amount in the axis's OWN unit.
 *
 * ⚠⚠ THERE ARE THREE UNITS, NOT TWO. `unit` is `get_org_usage`'s sixth column and it says how the
 * row's `used` / `included` / `overage` numbers are denominated: `'bytes'`, `'count'`, and — since
 * router#932 — `'cents'`. **Compute's unit MUST be money and cannot be hours**, because compute is
 * quoted per hour BY MACHINE CLASS and drawn against ONE dollar allowance shared across classes:
 * 25 small-hours and 25 medium-hours are not the same allowance, so no number of hours names what
 * the customer has left. Cents is the only denomination that stays true when a second class draws
 * on the same pool, which is why `plans.compute_allowance_cents` is cents while the marketing copy
 * advertises hours of `small`. Do not "simplify" this three-way dispatch back to two.
 *
 * ⚠ `get_org_usage` returns a `unit` per axis for this reason, so dispatch on the unit and never
 * on the axis name. A byte formatter prints "1.2 KB" for twelve dollars.
 *
 * An unknown unit falls back to a bare number followed by the unit's own name, never to bytes:
 * the next unit the router adds should read as unstyled rather than as a fabricated size.
 *
 * ⚠ Money under half a cent that is not zero prints `< $0.01`, never `$0.00` (app#327). Two
 * minutes on `small` cost 0.07¢, and `$0.00` in the Compute bar or the chart tooltip says the
 * machine ran for free while the Compute card below prints `< $0.01` for the same amount.
 */
export const formatAxisAmount = (unit: string, value: number): string => {
  if (unit === 'bytes') return formatBytes(value)
  if (unit === 'cents') return value > 0 && value < 0.5 ? '< $0.01' : formatCents(value)
  if (unit === 'count') return String(Math.round(value))
  return `${Math.round(value)} ${unit}`
}

/**
 * A round amount on a chart's scale, or an allowance named in a sentence: `formatAxisAmount` with
 * no ".0". "200 MB" and "250 GB", where a measured amount reads "383.6 MB". Money is not changed.
 */
export const formatAxisTick = (unit: string, value: number): string =>
  formatAxisAmount(unit, value).replace(/\.0(?= )/, '')

/**
 * How full an axis's pool is, as a fraction of its allowance. 1 is exactly full; above 1 is over.
 *
 * ⚠⚠ THREE STATES, NOT TWO, AND THE THIRD IS THE ONE THAT GETS LOST. `included` is
 * `null` ⇒ UNLIMITED (a non-metered plan: no pool, so no fraction and no arc), and `0` ⇒ a REAL
 * ALLOWANCE OF ZERO. The router keeps those two apart deliberately: `get_org_usage` maps
 * `node_limit <= 0` and `storage_bytes_free <= 0` to NULL because unlimited is what those columns
 * mean, and REFUSES the same arm for bandwidth and compute, because "this plan includes no free
 * compute" is a tier we could sell. `org_limit_overrides` allows a value of 0 today
 * (`org_limit_overrides_value_nonneg`), so an org can carry a zero compute pool right now.
 *
 * ⚠ The shape that hides it is `included && included > 0 ? used / included : 0` — two predicates
 * over three states, which routes the zero-allowance org into the unlimited branch. It then draws
 * an EMPTY GREEN bar and an EMPTY ring under a red "allowance used up" caption: the panel reports
 * healthy and over at the same time, and the reader decides neither is real.
 *
 * ⚠ One definition for the bar and the ring. They had two spellings of this arithmetic and both
 * had the defect; two copies are how the next correction reaches only one of them.
 */
export const allowanceFraction = (used: number, included: number | null): number => {
  if (included == null) return 0 // unlimited — no pool to be a fraction of
  if (included > 0) return used / included
  return used > 0 ? Infinity : 0 // a pool of zero: any use at all is fully over
}

/**
 * The scale an UNLIMITED axis's ring is drawn against, so the ring moves as the count grows.
 * `allowanceFraction` gives 0 for an unlimited axis, and the Nodes ring stayed empty at any count.
 *
 * The scale is the smallest of 10, 20 … 90, 100, 200 … 900, 1000, 2000 … that keeps `used` BELOW
 * 90% of it. After the first step the ring is between about 45% and 90% full.
 *
 * ⚠⚠ THIS IS NOT A LIMIT, AND NOTHING SHOWS IT AS ONE. It is never written on the page: a "20"
 * beside a Nodes ring reads as a plan limit, and no offered plan has one. The bar beside the ring
 * still says "Unlimited". The ring stays below 90%, so it never fills and never glows as over.
 */
export const unlimitedScale = (used: number): number => {
  if (!Number.isFinite(used) || used < 0) return 10 // no loop on a value that cannot be counted
  let digit = 1
  let unit = 10
  // `used * 10 >= 9 * scale` is `used >= 0.9 * scale` in integers, with no rounding at the edge.
  // ⚠ Stop when `unit` overflows: past it both sides are Infinity and the test stays true forever.
  while (Number.isFinite(unit) && used * 10 >= 9 * digit * unit) {
    digit++
    if (digit === 10) {
      digit = 1
      unit *= 10
    }
  }
  return digit * unit
}

/**
 * How full a RING is. The same as `allowanceFraction`, except that an unlimited axis is drawn
 * against `unlimitedScale`. The bar keeps `allowanceFraction`: it shows the numbers, and for an
 * unlimited axis it says "Unlimited".
 */
export const ringFraction = (used: number, included: number | null): number =>
  included == null ? used / unlimitedScale(used) : allowanceFraction(used, included)

/**
 * The nodes axis carries TWO counts on the usage panel, and they answer two questions (app#209).
 *
 * ⚠⚠ NOTHING HERE BILLS, AND THE WORD "BILLED" WAS A FALSE CLAIM ABOUT MONEY (Benoit, 2026-09-05:
 * *"the UI says billed nodes when the number of nodes is unlimited"*). **Devices bill on no plan.**
 * Migration `0108` dropped `node_overage_cents`, `get_billable_usage_admin`'s
 * `computed_overage_cents` sums bandwidth and compute ONLY, and its own comment says *"storage and
 * devices are stocks and stay plan limits"*. `0119` then set Free's `node_limit` to 0 — unlimited —
 * so no offered plan even caps them. The panel was telling a customer their 16 devices were being
 * charged for against a limit that does not exist.
 *
 * ⚠ The two COUNTS are still two facts and still need reconciling. `get_org_usage`'s `nodes` axis
 * is the org's COUNTED total: a node counts after 24 h cumulative online, and one deleted
 * mid-period that already crossed 24 h still counts. `get_usage_summary`'s `node_count_peak` is a
 * different fact — nodes ACTIVE in the period, meaning they produced at least one metrics row,
 * whether or not they have crossed 24 h.
 *
 * So a header reading "15 nodes" above a network row reading "20 nodes" is not a contradiction. It
 * is two facts sharing one word, which is what this states, the same way `reconcileStorage` states
 * the storage arithmetic: a total broken into rows must add up to the total (app#135).
 *
 * ⚠ SUMMING THE NETWORK ROWS IS SAFE, and only since router migration 0141. `get_usage_summary`
 * groups the per-node facts BY NODE first and attributes the result to at most one network
 * (`node_billing_network_id`), so a node in three networks contributes one. Before 0141 the same
 * sum double-counted it, and no arithmetic here could have told.
 *
 * ⚠⚠ THE DIFFERENCE RUNS BOTH WAYS AND BOTH DIRECTIONS ARE REPORTED. A node whose owner org has
 * no network to show it under is dropped from every network row (`network_id is null`) while it
 * still counts on the org axis, so counted can exceed active. Reporting only the expected direction
 * is how a screen comes to insist the unexpected case cannot happen.
 *
 * ⚠ There is no tolerance here and `reconcileStorage`'s has no analogue. Storage compares three
 * RPC reads of a moving rollup, so an exact match would report a clock as a mismatch; these are
 * whole devices, and a fraction of a device is not a reading — a difference of one is one node
 * with a reason, and naming it is the whole point.
 */
export type NodeCount = { nodeCountPeak: number }

export type NodeReconciliation = {
  /** Nodes active in the org's networks this period — one per node since router 0141. */
  activeNodes: number
  /** The nodes axis from `get_org_usage` — the ring. ⚠ NOT the invoice: devices bill on no plan. */
  countedNodes: number
  /** Active but not counted yet: they have not reached 24 h cumulative online. */
  notYetCounted: number
  /** Counted but named by none of the rows — a node attributed to no network of the org. */
  unattributedNodes: number
  /** The two counts agree, so the rows may be presented as adding up. */
  reconciles: boolean
}

/**
 * Reconcile the per-network node rows against the org's counted nodes axis.
 *
 * `countedNodes` is passed in rather than derived, for the reason `reconcileStorage` takes its
 * ring: the axis is what the ring draws and the rows are what the customer is shown, and comparing
 * the two is the job.
 */
export const reconcileNodes = (
  networkRows: readonly NodeCount[],
  countedNodes: number
): NodeReconciliation => {
  const activeNodes = networkRows.reduce(
    (total, row) => total + (Number.isFinite(row.nodeCountPeak) ? Math.round(row.nodeCountPeak) : 0),
    0
  )
  const counted = Number.isFinite(countedNodes) ? Math.round(countedNodes) : 0
  return {
    activeNodes,
    countedNodes: counted,
    notYetCounted: Math.max(0, activeNodes - counted),
    unattributedNodes: Math.max(0, counted - activeNodes),
    reconciles: activeNodes === counted,
  }
}

/**
 * The human label for an axis, and the theme palette slot it is drawn in.
 *
 * ⚠⚠ ONE DEFINITION, FOR THE SAME REASON `allowanceFraction` IS ONE. The rings, the bars and the
 * over-time chart all name and colour the same axes, and they had two spellings of both maps: a
 * new theme colour for compute reached the rings and left the chart on the old one, so one axis
 * was two colours down one page. `AXIS_PALETTE` holds the palette KEY rather than a resolved
 * colour, because the resolved colour needs a theme and this module must stay importable by
 * `node --experimental-strip-types`.
 *
 * ⚠ An unknown axis has no entry in either. Callers fall back to the axis's own name and to the
 * primary colour, so the next axis the router adds reads as unstyled rather than as something it
 * is not.
 */
export const AXIS_LABEL: Record<string, string> = {
  bandwidth: 'Bandwidth',
  compute: 'Compute',
  storage: 'Storage',
  nodes: 'Nodes',
}

export const AXIS_PALETTE: Record<string, 'error' | 'warning' | 'success' | 'info'> = {
  bandwidth: 'error',
  compute: 'warning',
  nodes: 'success',
  storage: 'info',
}
